import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Field, Formik } from 'formik'
import { isEmpty, isNil, isEqual, groupBy, uniq } from 'lodash'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { IconAlertCircle } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { LlmModelSelect } from '@/components/forms/formik/selects/LlmModelSelect'
import { FormField } from '@/components/forms/FormField'
import {
  saveOrgDefaultBitFlow,
  viewOrgDefaultFlowVersion,
  REMOVED_DEFAULT_FIELD_STRATEGY,
} from '@/reducers/playbooks/playbook.actions'
import { setRTNOrgDefaultRemovalPreview } from '@/reducers/playbooks/playbook.redux'

import {
  getDuplicateFieldKeys,
  getInvalidValueMessage,
  hasPromptMissingRequiredTokens,
} from '../helpers'
import { PromptFieldRows } from './PromptFieldRows'
import { PromptEditorFields } from './PromptEditorFields'
import { NotetakerVersionSelector } from './NotetakerVersionSelector'

// Editor for the org's default bit flow (no playbook / call-type scope).
// Fields added here — any type the guided modal can create — and the
// descriptions saved with them cascade to every channel and playbook flow that
// inherits them. NotetakerPage owns fetching the flow and showing this view;
// the component itself renders nothing when no default flow is loaded.
//
// The header's version dropdown can put an older saved version on screen, in
// which case the editor is read-only throughout — the one action left is
// restoring that version, which saves its fields as the new defaults.
const OrgDefaultsFormComponent = ({ flags }) => {
  const dispatch = useDispatch()
  const {
    rtnOrgDefaultFlow,
    rtnOrgDefaultFlowVersions = [],
    rtnOrgDefaultViewedVersion,
    rtnOrgDefaultViewedPrompt,
    rtnOrgDefaultViewedPromptFailed,
    rtnOrgDefaultPrompt,
    rtnOrgDefaultRemovalPreview,
    rtnModelOptions,
    rtnOrg,
    loading,
  } = useSelector((state) => state.playbook)
  const { organizationid } = useSelector((state) => state.currentUser)
  const playbookOptions = useSelector((state) => state.analytics.data.playbooks)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [showRestoreVersionModal, setShowRestoreVersionModal] = useState(false)

  const isAdmin = organizationid === 1
  // Admins manage other orgs, so save to the org they selected; everyone else
  // saves their own org's defaults.
  const effectiveOrgId = isAdmin ? rtnOrg : organizationid

  // Nothing renders until the API has returned a default flow with fields —
  // orgs without one never see this section. Keyed on the live default (which
  // gates this whole view) rather than on whichever version is being shown.
  if (loading.orgDefaultFlow || isNil(rtnOrgDefaultFlow) || isEmpty(rtnOrgDefaultFlow.fields)) {
    return null
  }

  // An older version is history: viewable, never edited in place, so the whole
  // editor locks. It can be restored, though — that deliberately cascades its
  // descriptions to every inheriting playbook, which is what makes it worth
  // confirming (and why it goes through the same removal-preview flow as a
  // normal save).
  const isOldVersion = !isNil(rtnOrgDefaultViewedVersion)
  const viewedFlow = rtnOrgDefaultViewedVersion ?? rtnOrgDefaultFlow
  // An older version shows the prompt it ran on once that's loaded. Before then
  // (or if it can't be read) the current prompt stands in, and restoring waits —
  // otherwise it would quietly put back the current prompt.
  const viewedPrompt = (isOldVersion && rtnOrgDefaultViewedPrompt) || rtnOrgDefaultPrompt
  const viewedPromptLoading = isOldVersion && loading.rtnOrgDefaultViewedPrompt
  const viewedPromptFailed = isOldVersion && rtnOrgDefaultViewedPromptFailed

  const handleVersionSelect = (versionId) => {
    const selected = rtnOrgDefaultFlowVersions.find((version) => version.id === versionId)
    // Fall back to the live default for the current version rather than its
    // history row — nothing sits above the default to inherit from, so the two
    // agree, and the editor should save against the flow it loaded.
    dispatch(viewOrgDefaultFlowVersion(selected?.is_active ? null : selected))
  }

  const sortedFields = [...viewedFlow.fields].sort(
    (fieldA, fieldB) => (fieldA.order_id ?? 0) - (fieldB.order_id ?? 0)
  )
  // Carry each field's stored config through the form so saving preserves
  // attributes this editor doesn't touch (type, prompt, taxonomy, display).
  const initialValues = {
    prompt_fields: sortedFields.map((field) => ({
      // field_name/description can come back null; coalesce so downstream
      // string ops (buildPrompt, .length, row rendering) don't crash.
      key: field.field_name ?? '',
      value: field.description ?? '',
      prompt_uuid: field.prompt_uuid,
      data_type: field.data_type,
      field_type: field.field_type,
      taxonomy_uuid: field.taxonomy_uuid,
      compare_value: field.compare_value,
      show_to_user: field.show_to_user,
    })),
    // The defaults editor doesn't offer gating conditions yet, but the shared
    // rows read this key — default it here rather than inside the component.
    field_links: [],
    llm_model_id: viewedPrompt?.llm_model_id || null,
    system_prompt: viewedPrompt?.system_prompt || '',
    user_prompt: viewedPrompt?.user_prompt || '',
    max_response_tokens: viewedPrompt?.max_response_tokens ?? null,
  }

  const formattedModelOptions = rtnModelOptions.map((option) => ({
    value: option.llm_model_id,
    label: option.display_name,
    description: option.description,
    deprecated: option.deprecated,
  }))

  const saveDefaults = async (values, setFieldValue, removedFieldStrategy) => {
    // The save hands back the taxonomies it minted for newly added fields.
    const { mintedTaxonomyUuidByIndex = {} } =
      (await dispatch(
        saveOrgDefaultBitFlow({
          organizationId: effectiveOrgId,
          promptFields: values.prompt_fields,
          llmModelId: values.llm_model_id,
          systemPrompt: values.system_prompt,
          userPrompt: values.user_prompt,
          maxResponseTokens: values.max_response_tokens,
          removedFieldStrategy,
        })
      )) ?? {}
    // A removal decision bounces the save back and the keep/delete retry submits
    // these same values, so link each newly added field to the taxonomy the save
    // minted for it — otherwise the retry mints a second one and orphans the
    // first. Reinitialization is off while the modal is open, so this sticks.
    Object.entries(mintedTaxonomyUuidByIndex).forEach(([index, taxonomyUuid]) => {
      setFieldValue(`prompt_fields.${index}.taxonomy_uuid`, taxonomyUuid)
    })
  }

  // Each preview warning is one (removed field, impacted playbook) pair —
  // group by field so the modal reads "this field is used by these playbooks".
  const removalPreview = rtnOrgDefaultRemovalPreview || []
  const playbookNameByCid = Object.fromEntries(
    playbookOptions.map((option) => [option.value, option.label])
  )
  const removalsByField = groupBy(removalPreview, 'field_name')
  const dismissRemovalPreview = () => dispatch(setRTNOrgDefaultRemovalPreview(null))

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setFieldValue }) => saveDefaults(values, setFieldValue)}
      // Don't reinitialize mid-save or while the removal modal is open: saving
      // versions the default prompt, which changes initialValues and would
      // otherwise wipe the user's in-progress field edits — so the modal's
      // keep/delete retry would submit the reset values and silently no-op.
      enableReinitialize={!loading.saveOrgDefaultFlow && isEmpty(removalPreview)}
    >
      {({ handleSubmit, values, setFieldValue }) => {
        // This standalone Formik has no validationSchema, so guard the field
        // name here too — a blank name would POST field_name: '' and cascade an
        // empty-named default to every inheriting playbook.
        // Trimmed, so whitespace-only can't pass as filled in.
        const hasEmptyFieldNames = values.prompt_fields.some((field) => isEmpty(field.key.trim()))
        const invalidValues = values.prompt_fields.filter(
          (field) => isEmpty(field.value.trim()) || field.value.length > 5000
        )
        // A field name may only appear once per configuration.
        const hasDuplicateFieldNames = !isEmpty(getDuplicateFieldKeys(values.prompt_fields))
        // The default prompt must reference $bit_json and $transcript once a real
        // record backs it.
        const promptMissingTokens = hasPromptMissingRequiredTokens(values, {
          sharedRequired: isAdmin && Boolean(rtnOrgDefaultPrompt?.prompt_uuid),
        })
        // Why Save (or Restore This Version) is blocked, surfaced as the
        // button's tooltip — a field added through the guided modal arrives
        // without a description, so say so rather than leaving a dead button.
        // Mirrors the playbook editor.
        const getSaveDisabledReason = () => {
          if (hasEmptyFieldNames) {
            return 'Every field needs a name'
          }
          if (hasDuplicateFieldNames) {
            return 'Field names must be unique'
          }
          if (!isEmpty(invalidValues)) {
            return getInvalidValueMessage(invalidValues)
          }
          if (promptMissingTokens) {
            return 'Each prompt must include $bit_json and $transcript'
          }
          if (viewedPromptLoading) {
            return "Loading this version's prompt"
          }
          if (viewedPromptFailed) {
            return "Couldn't load this version's prompt - switch versions and back to retry"
          }
          return ''
        }
        const saveDisabledReason = getSaveDisabledReason()
        // Only offer the restore once the fields diverge from what was loaded.
        // Compares only prompt_fields — the model lives in its own control.
        const hasFieldChanges = !isEqual(values.prompt_fields, initialValues.prompt_fields)

        const handleRestoreFields = () => {
          setFieldValue('prompt_fields', initialValues.prompt_fields)
          setShowRestoreModal(false)
        }

        const handleModelChange = (selectedModel, action) => {
          setFieldValue(
            'llm_model_id',
            action.action === 'clear' ? null : selectedModel?.value ?? null
          )
        }

        return (
          <form className="rtn-edit-form field-stack" onSubmit={handleSubmit}>
            <div className="rtn-editor__prompt user-prompt" data-testid="org-defaults-section">
              <div className="user-prompt-headline">
                <div>
                  <h2>Organization Defaults</h2>
                  <p className="muted-text">
                    These are your default Notetaker fields. Their descriptions apply to every
                    channel and playbook that uses them. When creating a new playbook, these are
                    automatically assigned.
                  </p>
                </div>
                <div className="flex-align-end small-gap">
                  {hasFieldChanges && !isOldVersion && (
                    <div data-testid="restore-org-defaults-button">
                      <Button basic type="button" onClick={() => setShowRestoreModal(true)}>
                        Restore Fields
                      </Button>
                    </div>
                  )}
                  <NotetakerVersionSelector
                    versions={rtnOrgDefaultFlowVersions}
                    selectedId={viewedFlow.id}
                    loading={loading.rtnOrgDefaultFlowVersions}
                    onSelect={handleVersionSelect}
                  />
                </div>
              </div>

              {isOldVersion && (
                <Banner
                  dataTestId="rtn-org-defaults-read-only-banner"
                  informative
                  header="You are viewing an older version of your organization defaults."
                  icon={<IconAlertCircle />}
                >
                  <p>
                    Its fields are read-only. Restore this version to make it your defaults again,
                    or go back to the current ones to keep editing.
                  </p>
                  <div className="flex-start small-gap mt">
                    <Button
                      basic
                      type="button"
                      data-testid="return-to-current-defaults-btn"
                      onClick={() => dispatch(viewOrgDefaultFlowVersion(null))}
                    >
                      Back to Current Version
                    </Button>
                  </div>
                </Banner>
              )}

              <PromptFieldRows
                setFieldValue={setFieldValue}
                values={values}
                flags={flags}
                groupByAgentVisibility
                readOnly={isOldVersion}
                isOrgDefaultFlow
              />

              {/* Only show the model selector when a backing prompt is loaded.
                  Without one (default fields carry no prompt_id), a model
                  change has no prompt version to write to and would be
                  silently dropped on save. On an older version this is the
                  prompt that version ran on — shown, but locked. */}
              {isAdmin && !isNil(rtnOrgDefaultPrompt) && (
                <div className="rtn-prompt__additional-details rtn-overall-model">
                  <FormField data-testid="org-defaults-model-container">
                    <label>Default AI Model</label>
                    <Field
                      data-testid="org-defaults-model-dropdown"
                      name="llm_model_id"
                      component={LlmModelSelect}
                      options={formattedModelOptions}
                      onChange={handleModelChange}
                      loading={loading.rtnModelOptions}
                      isDisabled={isOldVersion}
                    />
                  </FormField>
                  <PromptEditorFields
                    testId="rtn-org-defaults-prompt-editor"
                    required={Boolean(rtnOrgDefaultPrompt?.prompt_uuid)}
                    readOnly={isOldVersion}
                  />
                </div>
              )}
            </div>

            <div className="flex-end small-gap save-button-container">
              <div data-testid="save-org-defaults-button">
                <Button
                  type="button"
                  primary
                  disabled={!isEmpty(saveDisabledReason)}
                  disabledTooltip={saveDisabledReason}
                  loading={loading.saveOrgDefaultFlow}
                  onClick={() => {
                    // On an older version the form already holds that version's
                    // fields, so restoring is this same save — only confirmed
                    // first, since it rewrites what every inheriting playbook
                    // uses.
                    if (isOldVersion) {
                      setShowRestoreVersionModal(true)
                      return
                    }
                    handleSubmit()
                  }}
                >
                  {isOldVersion ? 'Restore This Version' : 'Save Defaults'}
                </Button>
              </div>
            </div>

            <BasicModal
              show={showRestoreModal}
              onClose={() => setShowRestoreModal(false)}
              title="Are you sure you want to restore fields?"
              size="tiny"
            >
              {showRestoreModal && (
                <>
                  <div className="mb">
                    Restoring will overwrite your current changes. This action cannot be undone. Do
                    you want to proceed?
                  </div>

                  <div className="modal-footer">
                    <Button
                      type="button"
                      data-testid="restore-cancel-button"
                      onClick={() => setShowRestoreModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      primary
                      type="button"
                      data-testid="restore-confirm-button"
                      onClick={handleRestoreFields}
                    >
                      Yes, Restore Fields
                    </Button>
                  </div>
                </>
              )}
            </BasicModal>

            <BasicModal
              show={showRestoreVersionModal}
              onClose={() => setShowRestoreVersionModal(false)}
              title="Restore this version?"
              size="tiny"
            >
              {showRestoreVersionModal && (
                <>
                  <div className="mb">
                    This saves these fields as your organization defaults, so every playbook that
                    inherits them picks up these descriptions. Nothing is lost - the version it
                    replaces stays in the history.
                  </div>

                  <div className="modal-footer">
                    <Button
                      type="button"
                      data-testid="cancel-restore-version-button"
                      onClick={() => setShowRestoreVersionModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      primary
                      type="button"
                      data-testid="confirm-restore-version-button"
                      onClick={() => {
                        setShowRestoreVersionModal(false)
                        handleSubmit()
                      }}
                    >
                      Yes, Restore This Version
                    </Button>
                  </div>
                </>
              )}
            </BasicModal>

            <BasicModal
              show={!isEmpty(removalPreview)}
              onClose={dismissRemovalPreview}
              title="This field is used by other playbooks"
              size="tiny"
            >
              {!isEmpty(removalPreview) && (
                <>
                  <div className="mb" data-testid="removal-preview-body">
                    The following fields are inherited by other playbooks. Keeping them copies the
                    current default into each playbook so they keep working. Removing them deletes
                    the field from those playbooks too.
                    <ul>
                      {Object.entries(removalsByField).map(([fieldName, warnings]) => {
                        const playbooks = uniq(
                          warnings.map(
                            (warning) =>
                              playbookNameByCid[warning.playbook_cid] ||
                              warning.playbook_cid ||
                              'another configuration'
                          )
                        )
                        return (
                          <li key={fieldName}>
                            <strong>{fieldName}</strong>: {playbooks.join(', ')}
                          </li>
                        )
                      })}
                    </ul>
                  </div>

                  <div className="modal-footer">
                    <Button
                      type="button"
                      data-testid="removal-cancel-button"
                      onClick={dismissRemovalPreview}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      data-testid="removal-keep-button"
                      loading={loading.saveOrgDefaultFlow}
                      onClick={() =>
                        saveDefaults(values, setFieldValue, REMOVED_DEFAULT_FIELD_STRATEGY.KEEP)
                      }
                    >
                      Keep in Playbooks
                    </Button>
                    <Button
                      critical
                      type="button"
                      data-testid="removal-delete-button"
                      loading={loading.saveOrgDefaultFlow}
                      onClick={() =>
                        saveDefaults(values, setFieldValue, REMOVED_DEFAULT_FIELD_STRATEGY.DELETE)
                      }
                    >
                      Remove from All Playbooks
                    </Button>
                  </div>
                </>
              )}
            </BasicModal>
          </form>
        )
      }}
    </Formik>
  )
}

export const OrgDefaultsForm = withLDConsumer()(OrgDefaultsFormComponent)
